<template>
  <v-expansion-panels
    v-model="expanded"
    class="expansion-panels"
    bg-color="primary"
    flat
    dark
    :hide-actions="!hasSummary"
  >
    <v-expansion-panel class="expansion-panel-border">
      <v-expansion-panel-title>
        <div
          :class="{
            'align-center': !hasSummary,
            'flex-column flex-md-row justify-md-space-between': hasSummary,
          }"
          class="w-full d-flex align-md-center justify-space-between text-white"
        >
          <span>
            <h4
              class="title"
              v-if="hasSummary"
            >
              {{ t('lead.ai-summary.title.0') }}
            </h4>
            <h4
              class="title"
              v-else
            >
              {{ t('lead.ai-summary.title.1') }}
            </h4>
          </span>
          <span>
            <template v-if="hasSummary">
              <span class="date">
                {{ formatDate(leadSummary?.generatedAt, DATE_LONG) }}
              </span>
              <span class="mx-2">|</span>
              <v-btn
                variant="text"
                class="px-0 btn"
                color="white"
                :disabled="leadSummaryLoading || showUpselling"
                :loading="leadSummaryLoading"
                @click.stop="createSummary()"
              >
                <v-icon alt="Aktualisieren"> mdi-reload </v-icon
                >{{ t('lead.ai-summary.btn.label-recreate') }}
              </v-btn>
              <span class="mx-2">|</span>
            </template>
            <v-btn
              v-else
              color="white"
              :style="{ color: primaryColor + '!important' }"
              class="btn-create btn"
              :disabled="leadSummaryLoading || showUpselling"
              :loading="leadSummaryLoading"
              @click.stop="createSummary()"
            >
              {{ t('lead.ai-summary.btn.label-create') }}
            </v-btn>
          </span>
        </div>
      </v-expansion-panel-title>
      <BlurredBackdropUpselling v-if="showUpselling" />
      <v-expansion-panel-text
        class="text-white summary"
        v-if="hasSummary && !showUpselling"
      >
        <span v-html="parsedSummary" />
      </v-expansion-panel-text>
    </v-expansion-panel>
  </v-expansion-panels>
</template>
<script setup lang="ts">
import { useMutation } from '@vue/apollo-composable'
import DOMPurify from 'dompurify'
import { marked } from 'marked'
import { computed, inject, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { useTheme } from 'vuetify'

import { AuthInstance } from '@/auth'
import BlurredBackdropUpselling from '@/components/BlurredBackdropUpselling.vue'
import { isExpertUser } from '@/lib/productTypes'
import { showSnackbarMessage } from '@/lib/snackbarMessages'
import { TrackingPlugin } from '@/plugins/tracking'

import { DATE_LONG, formatDate } from '../../../lib/dateTime'
import LEAD_AI_SUMMARY from './queries/leadAiSummary.gql'

const { t } = useI18n()
const theme = useTheme()
const primaryColor = theme.current.value.colors.primary
const $tracking = inject<TrackingPlugin>('tracking')
const $auth = inject<AuthInstance>('auth')
const props = defineProps({
  isMasterView: Boolean,
  lead: Object,
})
const leadSummary = ref(props.lead?.aiSummary || null)
const showUpselling = ref(false)
const expanded = ref<number | null>(null)
const isExpert = computed(() => isExpertUser($auth?.user))
const parsedSummary = computed(() => {
  const md = leadSummary.value?.summary ?? ''
  const html = marked.parse(md)

  if (typeof html === 'string') {
    return DOMPurify.sanitize(html)
  }

  return ''
})

const createSummary = async () => {
  expanded.value = 0
  if (!isExpert.value) {
    showUpselling.value = true
    return
  }
  $tracking.event('Lead', 'Clicked', 'Create AI Summary')

  const result = await leadSummaryRequest()
  if (!result?.data?.leadAiSummary) {
    showSnackbarMessage('error', t('lead.ai-summary.alert.create-error'))
    return
  }
  leadSummary.value = {
    ...result.data.leadAiSummary,
  }
  showSnackbarMessage('success', t('lead.ai-summary.alert.create-success'))
}

const { loading: leadSummaryLoading, mutate: updateLeadSummary } = useMutation(LEAD_AI_SUMMARY)

const leadSummaryRequest = async () => {
  try {
    const summary = await updateLeadSummary({
      input: {
        leadId: props?.lead?.id,
      },
    })
    $tracking.event('Lead', 'Created', 'Create AI Summary')
    return summary
  } catch (error) {
    leadSummary.value = {
      summary: null,
      generatedAt: null,
    }
    showSnackbarMessage('error', t('lead.ai-summary.alert.create-error'))
    return null
  }
}

const hasSummary = computed(() => {
  return !!leadSummary.value?.summary
})
</script>

<style scoped>
.expansion-panels :deep(.v-expansion-panel-title__icon) {
  color: white !important;
}
.title,
.date {
  font-weight: 500;
  font-size: 18px;
  line-height: 26px;
  letter-spacing: 0;
  vertical-align: middle;
  text-transform: none !important;
}

.date,
.btn {
  font-size: 16px;
}
.summary {
  font-weight: 400;
  font-size: 16px;
  line-height: 22px;
  letter-spacing: 0;
  vertical-align: middle;
}
</style>
